/**
 * useOpenAppModal — Pre-open validation + routing for app creation.
 *
 * Encapsulates the full scio2 openAppModal → _openAppModal flow:
 * 0. Folder security check (shared folder confirmation)
 * 1. Canvas short-circuit (redirect to design page)
 * 2. Enterprise-only validation (featureKey gated → warning toast)
 * 3. Per-app creation limit (limitationCreateAsset → count-limit dialog)
 * 4. Plan lock → subscription modal (upgrade from toast to real modal)
 * 5. SmallApp editor or scio1 fallback routing
 *
 * Used by AppSelectorDialog to replace inline handleSelectApp logic.
 *
 * @see claude-dev/2026-02-09-screen-ted/final-document/03-assets/app-dialog.md — Layer 3 spec
 * @see scio2: optisigns-portal-common SmallappUIService._openAppModal
 */
import { useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useApolloClient } from '@apollo/client/react';
import { useDecodedPath } from '@/hooks/useDecodedPath';
import { isRootPath, useRootCreateLock } from '@/contexts/RootActionLockContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { AppMeta, AssetGroup } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useSmallAppEditorStore, type SmallAppEditorOptions } from '@/stores/smallapp-editor-store';
import { useNewItemHandler, useNewItemCacheHandler } from '@/hooks/new-item-context';
import { useDesignerEditorStore } from '@/stores/designer-editor-store';
import { useAppSelectorStore } from '@/stores/app-selector-store';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { useScio1ModalStore } from '@/stores/scio1-modal-store';
import { useSplitScreenDialogStore } from '../stores/split-screen-dialog-store';
import { useVideoWallDialogStore } from '../stores/video-wall-dialog-store';
import { useCurrentGroup } from './useCurrentGroup';
import { checkAppCatalogLock, isEnterpriseOnly } from '../utils/app-plan-check';
import { checkCreationLimit } from '../utils/app-creation-limit';
import { useCreationLimitStore } from '../stores/creation-limit-store';
import { isSmallAppEditable } from '../utils/app-meta';

/** Editor options a CALLER may attach to the smallapp editor this open creates.
 *  Deliberately narrow — the folder path, the current group and the after-create handler are
 *  the hook's own business and are never overridable from outside. Today the only field is
 *  `appIntent`, set by the app-intent launcher so the config dialog can recognise its editor. */
export type OpenAppModalEditorOptions = Pick<SmallAppEditorOptions, 'appIntent'>;

export interface UseOpenAppModalResult {
  /** Call with an AppMeta to run full validation + routing. `editorOptions` (optional) rides
   *  along to the smallapp editor's options bag, including across the shared-folder
   *  confirmation detour. */
  openAppModal: (app: AppMeta, editorOptions?: OpenAppModalEditorOptions) => void;
  /** Non-null when awaiting folder security confirmation */
  pendingFolderConfirm: AppMeta | null;
  /** Current folder group — for displaying security badges in confirmation dialog */
  currentGroup: AssetGroup | null;
  /** Refetch group data to ensure fresh security details */
  refetchGroup: () => void;
  /** Call when user confirms folder security dialog */
  onFolderSecurityConfirm: () => void;
  /** Call when user cancels folder security dialog */
  onFolderSecurityCancel: () => void;
  /** Non-null app name when enterprise-only warning dialog should be shown */
  enterpriseWarningApp: string | null;
  /** Dismiss the enterprise warning dialog */
  onEnterpriseWarningClose: () => void;
}

export function useOpenAppModal(): UseOpenAppModalResult {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appSelectorContext = useAppSelectorStore((s) => s.context);
  const decodedPath = useDecodedPath();
  const currentPath = appSelectorContext.currentPath || decodedPath || '/';
  const folderTeamId = searchParams.get('teamId') || undefined;
  // §12: would this member be refused at the Root? Checked against the path the pick resolves to
  // (the picker's own path wins over the page's) in `openAppModal`, before any editor opens.
  const rootLock = useRootCreateLock('/');

  const company = useAuthStore((s) => s.company);
  const me = useAuthStore((s) => s.me);
  const { folderLevelSecurity } = useCompanyFeatures();
  const { openSubscriptionPlan } = useSubscriptionModal();
  const { sendWithPromise, showModal, hideModal } = useScio1ModalStore();
  const newItemHandler = useNewItemHandler();
  const closeAppSelector = useAppSelectorStore((s) => s.closeAppSelector);
  const apolloClient = useApolloClient();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  // Fallback for when AppSelectorDialog is mounted outside a NewItemContext.Provider tree
  // (e.g., PlaylistsPage mounts AppSelectorDialog outside its provider). Uses the
  // appSelectorContext.currentPath set by AssetPickerPanel when openAppSelector is called.
  const fallbackItemHandler = useNewItemCacheHandler('assets', 'Asset', {
    path: currentPath === '/' ? null : currentPath,
    teamId: activeTeamId ?? '',
    engage: appSelectorContext.engage ?? false,
  });
  const effectiveNewItemHandler = newItemHandler ?? fallbackItemHandler;

  // Reuses the same Apollo-cached query as AssetsPage (no extra network call)
  const { currentGroup, refetch: refetchGroup } = useCurrentGroup(currentPath, folderTeamId);

  const currentPlan = company?.payment?.planFeature;
  const companyFeatures = useMemo(
    () => (company?.feature ?? {}) as Record<string, boolean>,
    [company],
  );

  // Skip "Create And Share" confirm on /share/* pages — user is already operating
  // through a share link, so the warning is redundant. Covers both FolderSharePage
  // and PlaylistSharePage (both mount AppSelectorDialog → useOpenAppModal).
  const isShareLinkContext = window.location.pathname.startsWith('/share/');

  const isSharedFolder = useMemo(
    () =>
      !isShareLinkContext &&
      folderLevelSecurity &&
      currentGroup != null &&
      ((currentGroup.securities ?? []).some((s) => s.userId !== '0') ||
        (currentGroup.securityFolderTeams != null && currentGroup.securityFolderTeams.length > 0) ||
        (currentGroup.securityGroupIds != null && currentGroup.securityGroupIds.length > 0)),
    [isShareLinkContext, folderLevelSecurity, currentGroup],
  );

  const isSubAccount = useMemo(
    () => Boolean(company?.featureData?.enableSubAccount && me?.accountId !== company?._id),
    [company, me],
  );

  // Folder security confirmation state
  const [pendingApp, setPendingApp] = useState<AppMeta | null>(null);
  const [pendingFromAssetPicker, setPendingFromAssetPicker] = useState(false);
  const [pendingCurrentGroup, setPendingCurrentGroup] = useState<AssetGroup | null>(null);
  const [pendingPickerPath, setPendingPickerPath] = useState<string | undefined>(undefined);
  // Held across the confirmation too: an app-intent launch into a shared folder must still
  // reach the editor as an intent launch once the user confirms.
  const [pendingEditorOptions, setPendingEditorOptions] = useState<
    OpenAppModalEditorOptions | undefined
  >(undefined);

  // Enterprise-only warning dialog state
  const [enterpriseWarningApp, setEnterpriseWarningApp] = useState<string | null>(null);

  // Per-app creation-limit dialog is global — driven by the store + the single
  // <CreationLimitDialog/> mounted in AppShell. Both the pre-check (below) and
  // the save-error echo push into the same store. Cap logic lives in
  // utils/app-creation-limit.ts.
  const showLimitNotice = useCreationLimitStore((s) => s.show);

  /** Core validation + routing logic (runs after folder security check passes) */
  const processApp = useCallback(
    async (
      app: AppMeta,
      opts?: {
        fromAssetPicker?: boolean;
        pickerPath?: string;
        editorOptions?: OpenAppModalEditorOptions;
      },
    ) => {
      // Step 2: Canvas short-circuit → open Designer
      // Matches scio2 SmallappUIService.openCanvas() for new canvas (no asset_id)
      if (app.appType === 'canvas') {
        // Share pages: open Designer in fullscreen dialog (avoids leaving share context)
        if (window.location.pathname.startsWith('/share/')) {
          const { useDesignerEditDialogStore } =
            await import('@/features/share-links/components/DesignerEditDialog');
          useDesignerEditDialogStore.getState().openDesignerEditor(null, currentPath ?? '/');
          return;
        }

        // Build shared params — folder path + teamId for all Designer open paths
        const designerParams = new URLSearchParams();
        if (currentPath && currentPath !== '/') designerParams.set('path', currentPath);
        const urlTeamId = new URLSearchParams(window.location.search).get('teamId');
        if (urlTeamId) designerParams.set('teamId', urlTeamId);

        // From asset picker: open Designer in a modal dialog instead of navigating.
        // The SelectAssetModal stays open behind the dialog — no need for saveForReopen.
        const designerUrl = import.meta.env.VITE_DESIGNER_URL;
        if (opts?.fromAssetPicker) {
          if (designerUrl) {
            useDesignerEditorStore.getState().openEditor({
              currentPath,
              params: Object.fromEntries(designerParams.entries()),
              onClose: () => useDesignerEditorStore.getState().closeEditor(),
            });
            return;
          }
          // Fallback: open Designer page in a new window if VITE_DESIGNER_URL is not configured
          const qs = designerParams.toString();
          window.open(`/app/designManagement${qs ? `?${qs}` : ''}`, '_blank');
          return;
        }

        // Non-picker: navigate to Designer page (Assets page, Engage, etc.)
        // Preserve the origin page so Designer navigates back here on close
        const currentRoute = window.location.pathname + window.location.search;
        if (currentRoute && !currentRoute.startsWith('/app/assetManagement')) {
          designerParams.set('returnTo', currentRoute);
        }
        const qs = designerParams.toString();
        navigate(`/app/designManagement${qs ? `?${qs}` : ''}`);
        return;
      }

      // Step 3: Validation chain

      // Branch 1: Enterprise-only (featureKey required but not enabled).
      // Runs before the creation-limit check so an enterprise app whose feature
      // isn't enabled at all shows "contact to enable" rather than a cap warning.
      if (isEnterpriseOnly(app, companyFeatures)) {
        setEnterpriseWarningApp(app.text ?? app.appType ?? 'Enterprise App');
        return;
      }

      // Branch 2: Per-app creation limit — unified virtualScreen + generic
      // limitationCreateAsset gate. resolveCreationLimit() owns where the cap
      // comes from; this just blocks when the team is already at it. Adding a
      // new capped app needs only AppMeta data, no change here.
      // See utils/app-creation-limit.ts.
      const notice = await checkCreationLimit(app, company, apolloClient, activeTeamId);
      if (notice) {
        showLimitNotice(notice);
        return;
      }

      // Branch 3: Generic plan lock → open subscription modal (not just a toast)
      const { isLocked, minPlanRequired } = checkAppCatalogLock(app, currentPlan, companyFeatures);
      if (isLocked) {
        if (isSubAccount) {
          toast.error(t('Please contact your account admin to upgrade your plan.'));
          return;
        }
        const isTrial = company?.payment?.isTrial ?? false;
        await openSubscriptionPlan({
          title: t('Change Plan'),
          subtitle: t('Upgrade Required'),
          action: isTrial ? 'changePlanTrialToPay' : 'changePlanPay',
          sizeModal: 'xl',
          params: {
            planFeature: currentPlan,
            minPlanRequired: minPlanRequired ?? '',
            subtitle: t('Upgrade Required'),
            arrCssFile: true,
          },
        });
        return;
      }

      // Step 4: Dialog routing

      // Split Screen → single-dialog flow. Opens the Zones dialog with
      // an empty `screenZones` list; the dialog renders its templates
      // rail full-width until the user picks a layout (Option A —
      // collapsed templates modal hop). See claude-dev/ted-documents/
      // 58-canvas-editor-ux/01-research.md, Phase F (entry consolidation).
      if (app.appType === 'splitScreen') {
        const effectivePath = opts?.pickerPath || currentPath;
        const activeTeamId = useTeamStore.getState().activeTeamId;
        useSplitScreenDialogStore.getState().openZones({
          mode: 'add',
          asset: {
            type: 'splitScreen',
            orientation: 'landscape',
            screenZones: [],
          },
          currentPath: effectivePath,
          currentTeamId: activeTeamId,
        });
        return;
      }

      // Video Wall → native two-step flow (Templates → Zones). Replaces
      // scio1 iframe `OPEN_APP` for `appType === 'videoWall'`. The
      // video-wall and split-screen flows are sibling but distinct:
      // separate stores, separate dialog components (per Phase 2 of
      // 57-video-wall spec). Asset-level discriminator on save is
      // `commonType === 'videoWall'` (the asset still has
      // `type === 'splitScreen'`).
      if (app.appType === 'videoWall') {
        const effectivePath = opts?.pickerPath || currentPath;
        const activeTeamId = useTeamStore.getState().activeTeamId;
        // Single-dialog flow: open Zones directly with an empty asset
        // and let the inline Templates tab handle layout selection.
        // (AddOnConfigDialog + Edit Screen still use openTemplates /
        // openTemplatesForLayout — those are embedded callback flows.)
        useVideoWallDialogStore.getState().openZones({
          mode: 'add',
          asset: {
            type: 'splitScreen',
            commonType: 'videoWall',
            orientation: 'landscape',
            unitType: 'percent',
            screenZones: [],
          },
          currentPath: effectivePath,
          currentTeamId: activeTeamId,
        });
        return;
      }

      // SmallApp → native SmallAppEditDialog
      // scio1: asset = new Asset({ appType: appMeta.appType }) — iframe needs appType on the asset
      if (isSmallAppEditable(app)) {
        // Use picker's browsed folder if available, otherwise fall back to page path (#834)
        const effectivePath = opts?.pickerPath || currentPath;
        useSmallAppEditorStore.getState().openEditor({
          appMeta: app,
          asset: { appType: app.appType },
          options: {
            // Caller fields first: the three below are the hook's own wiring and must win.
            ...opts?.editorOptions,
            currentPath: effectivePath,
            currentGroup: currentGroup ?? undefined,
            onAfterCreate: (newId, asset) => effectiveNewItemHandler(newId, { asset }),
          },
        });
        return;
      }

      // Feed apps → feeds page (scio1 iframe handles)
      if (app.appType === 'feed') {
        navigate('/app/feeds');
        return;
      }

      // Non-SmallApp fallback → open dialog in scio1 via modal adapter
      // Matches scio2: sendAdapter('open') → sendMessagePromise(OPEN_APP, data)
      // sendAdapter brings the scio1 adapter iframe to foreground; in scio3, showModal() does the same.
      if (sendWithPromise) {
        showModal(); // equivalent of scio2 sendAdapter('open')
        try {
          // Normalize params: scio1 adapter spreads params with {...params, isClose},
          // so string params (e.g. func.params="youtube") must be wrapped as {appType: value}
          // to match scio1's openWebModal({appType}) signature.
          const rawParams = app.func?.params;
          const normalizedParams =
            typeof rawParams === 'string' ? { appType: rawParams } : (rawParams ?? {});
          const result = await sendWithPromise('OPEN_APP', {
            name: app.func?.name ?? '',
            params: normalizedParams,
            currentPath: currentPath === '/' ? null : currentPath,
            currentGroup: currentGroup ?? undefined,
            isClose: false,
            features: companyFeatures,
          });
          // scio1 sends the created/updated asset back via CLOSE_ADAPTER({asset}), which
          // resolves the sendWithPromise. Pin it at position 0 so it's visible regardless
          // of sort order — without this, createdAt:ASC puts the new item at the bottom.
          if (result && typeof result === 'object' && '_id' in (result as object)) {
            const asset = result as { _id: string };
            effectiveNewItemHandler(asset._id, { asset });
          }
        } catch {
          // sendWithPromise may time out if scio1 closes without sending an asset response.
          // Swallow — WS subscription still updates the list. Refetch is handled by
          // Scio1Frame's CLOSE_ADAPTER handler.
        } finally {
          hideModal(); // equivalent of scio2 sendAdapter('close')
        }
      }
    },
    [
      navigate,
      companyFeatures,
      currentPlan,
      isSubAccount,
      company,
      openSubscriptionPlan,
      sendWithPromise,
      showModal,
      hideModal,
      currentPath,
      currentGroup,
      t,
      apolloClient,
      activeTeamId,
      showLimitNotice,
      effectiveNewItemHandler,
    ],
  );

  const openAppModal = useCallback(
    (app: AppMeta, editorOptions?: OpenAppModalEditorOptions) => {
      // Snapshot context before closeAppSelector() clears it
      const { fromAssetPicker, currentPath: pickerPath } = useAppSelectorStore.getState().context;

      // Close the app selector dialog first
      closeAppSelector();

      if (rootLock.locked && isRootPath(pickerPath || currentPath)) {
        toast.error(rootLock.reason);
        return;
      }

      const opts = { fromAssetPicker, pickerPath, editorOptions };

      // Step 0: Folder security check — if shared folder, show confirmation.
      // Snapshot currentGroup before the re-render wipes it (closeAppSelector)
      // clears currentPath → useCurrentGroup returns null) (#1000).
      if (isSharedFolder) {
        setPendingApp(app);
        setPendingFromAssetPicker(!!fromAssetPicker);
        setPendingCurrentGroup(currentGroup);
        setPendingPickerPath(pickerPath);
        setPendingEditorOptions(editorOptions);
        return;
      }

      processApp(app, opts);
    },
    [closeAppSelector, isSharedFolder, currentGroup, processApp, rootLock, currentPath],
  );

  const onFolderSecurityConfirm = useCallback(() => {
    const app = pendingApp;
    const fromAssetPicker = pendingFromAssetPicker;
    const pickerPath = pendingPickerPath;
    const editorOptions = pendingEditorOptions;
    setPendingApp(null);
    setPendingFromAssetPicker(false);
    setPendingCurrentGroup(null);
    setPendingPickerPath(undefined);
    setPendingEditorOptions(undefined);
    if (app) {
      processApp(app, { fromAssetPicker, pickerPath, editorOptions });
    }
  }, [pendingApp, pendingFromAssetPicker, pendingPickerPath, pendingEditorOptions, processApp]);

  const onFolderSecurityCancel = useCallback(() => {
    setPendingApp(null);
    setPendingCurrentGroup(null);
    setPendingPickerPath(undefined);
    setPendingEditorOptions(undefined);
  }, []);

  const onEnterpriseWarningClose = useCallback(() => {
    setEnterpriseWarningApp(null);
  }, []);

  return {
    openAppModal,
    pendingFolderConfirm: pendingApp,
    currentGroup: pendingCurrentGroup ?? currentGroup,
    refetchGroup,
    onFolderSecurityConfirm,
    onFolderSecurityCancel,
    enterpriseWarningApp,
    onEnterpriseWarningClose,
  };
}
